import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface RolePermission {
  id: string;
  organization_id: string;
  role: string;
  module: string;
  can_access: boolean;
  permissions: {
    read?: boolean;
    write?: boolean;
    delete?: boolean;
    export?: boolean;
    view_costs?: boolean;
  };
}

export function usePermissions() {
  const { user, currentOrganization } = useAuth();
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);

  const orgId = currentOrganization?.id;
  // Get role from users table data (enriched on auth)
  const userRole = (user as any)?.role || 'sales';
  const roleLevel = (user as any)?.role_level ?? 0;

  // Admin+ always bypasses permission checks
  const isAdmin = roleLevel >= 4;

  useEffect(() => {
    if (!orgId || !user) {
      setLoading(false);
      return;
    }

    let stale = false;

    async function fetchPermissions() {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .eq('organization_id', orgId)
        .eq('role', userRole);

      if (!stale) {
        if (error) {
          console.warn('Could not load permissions (table may not exist yet):', error.message);
          setPermissions([]);
        } else {
          setPermissions((data || []) as RolePermission[]);
        }
        setLoading(false);
      }
    }

    fetchPermissions();
    return () => { stale = true; };
  }, [orgId, userRole, user]);

  const canAccessPath = useCallback((path: string): boolean => {
    // Admin always has access
    if (isAdmin) return true;

    // No permissions loaded yet — allow (fail open during loading)
    if (permissions.length === 0) return true;

    // Check exact path match
    const exactMatch = permissions.find(p => p.module === path);
    if (exactMatch) return exactMatch.can_access;

    // Path aliases — new routes inherit from legacy routes they replaced.
    // Keeps existing role permissions working without DB migration.
    const ALIASES: Record<string, string> = {
      '/expenses': '/overhead-expenses',
    };
    if (ALIASES[path]) {
      const aliasMatch = permissions.find(p => p.module === ALIASES[path]);
      if (aliasMatch) return aliasMatch.can_access;
    }

    // Parent path fallback (e.g., /sales/123 → /sales)
    const parts = path.split('/').filter(Boolean);
    for (let i = parts.length - 1; i >= 1; i--) {
      const parentPath = '/' + parts.slice(0, i).join('/');
      const parentMatch = permissions.find(p => p.module === parentPath);
      if (parentMatch) return parentMatch.can_access;
    }

    // No permission found — deny by default
    return false;
  }, [permissions, isAdmin]);

  const hasPermission = useCallback((path: string, action: 'read' | 'write' | 'delete' | 'export' | 'view_costs'): boolean => {
    if (isAdmin) return true;

    const perm = permissions.find(p => p.module === path);
    if (!perm) {
      // Try parent path
      const parts = path.split('/').filter(Boolean);
      for (let i = parts.length - 1; i >= 1; i--) {
        const parentPath = '/' + parts.slice(0, i).join('/');
        const parentPerm = permissions.find(p => p.module === parentPath);
        if (parentPerm) return parentPerm.can_access && (parentPerm.permissions?.[action] ?? false);
      }
      return false;
    }

    return perm.can_access && (perm.permissions?.[action] ?? false);
  }, [permissions, isAdmin]);

  const canViewCosts = useCallback((): boolean => {
    if (isAdmin) return true;
    // Check if any cost-sensitive module grants view_costs
    return permissions.some(p =>
      p.can_access && p.permissions?.view_costs === true
    );
  }, [permissions, isAdmin]);

  return useMemo(() => ({
    canAccessPath,
    hasPermission,
    canViewCosts,
    isAdmin,
    loading,
    permissions,
    userRole,
    roleLevel,
  }), [canAccessPath, hasPermission, canViewCosts, isAdmin, loading, permissions, userRole, roleLevel]);
}
